import { Checkbox, Container, Section } from '@resolute/ui';
import { calculateStampDuty, formatCurrency, formatINRCompact } from '@resolute/utils';
import { useMemo, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';


import { CalcLayout, ResultStat, SliderField, ToolDisclaimer } from './components/calc-ui';

import { Seo } from '@/lib/seo';


export default function StampDutyCalculatorPage() {
  const [propertyValue, setPropertyValue] = useState(5_000_000);
  const [femaleOwner, setFemaleOwner] = useState(false);

  const { stampDuty, registrationFee, total } = useMemo(
    () => calculateStampDuty({ propertyValue, femaleRebatePct: femaleOwner ? 1 : 0 }),
    [propertyValue, femaleOwner],
  );

  const pie = [
    { name: 'Stamp duty', value: stampDuty },
    { name: 'Registration fee', value: registrationFee },
  ];
  const COLORS = ['hsl(var(--primary))', 'hsl(var(--muted-foreground))'];

  return (
    <Section>
      <Seo
        title="Stamp Duty Calculator"
        description="Estimate stamp duty and registration cost on a property purchase in Gujarat. Free stamp duty calculator for home buyers."
        path="/calculators/stamp-duty"
      />
      <Container className="max-w-5xl">
        <header className="mb-8">
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            Stamp Duty Calculator
          </h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Estimate the stamp duty and registration fee payable on a property purchase, based on
            Gujarat&apos;s commonly-cited rates.
          </p>
        </header>

        <CalcLayout
          controls={
            <>
              <SliderField
                label="Property value"
                value={propertyValue}
                onChange={setPropertyValue}
                min={500_000}
                max={100_000_000}
                step={100_000}
                display={formatINRCompact}
              />
              <Checkbox
                label="Sole female owner"
                checked={femaleOwner}
                onChange={(e) => setFemaleOwner(e.target.checked)}
                hint="1% rebate"
              />
            </>
          }
          results={
            <>
              <div>
                <ResultStat label="Total payable" value={formatCurrency(total)} emphasis />
              </div>

              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pie}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={48}
                      outerRadius={72}
                      paddingAngle={2}
                    >
                      {pie.map((entry, i) => (
                        <Cell key={entry.name} fill={COLORS[i]} stroke="none" />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v: number, n) => [formatCurrency(v), n as string]}
                      contentStyle={{
                        background: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <dl className="grid grid-cols-2 gap-4">
                <ResultStat label="Stamp duty" value={formatINRCompact(stampDuty)} tone="foreground" />
                <ResultStat
                  label="Registration fee"
                  value={formatINRCompact(registrationFee)}
                  tone="foreground"
                />
              </dl>
              <ToolDisclaimer>
                Indicative only, based on Gujarat&apos;s commonly-cited rates (4.9% stamp duty, 1%
                registration capped at ₹30,000) — actual rates vary by state, city, property type
                and owner category, and change by government notification. Confirm the current rate
                with your local sub-registrar before transacting.
              </ToolDisclaimer>
            </>
          }
        />
      </Container>
    </Section>
  );
}
